import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Calendar, Share2 } from "lucide-react";
import Seo, { SITE_ORIGIN } from "@/components/Seo";
import useApi from "@/hooks/useApi";
import usePreviewDraft from "@/hooks/usePreviewDraft";
import PreviewBanner from "@/components/PreviewBanner";
import { FadeUp } from "@/components/motion";

const SITE = SITE_ORIGIN;

const NewsDetail = () => {
  const { slug } = useParams();
  const { data: fetchedPost, error, loading } = useApi(`/news/${slug}`);
  const { data: all } = useApi(`/news`);
  const { draft, isPreview } = usePreviewDraft();
  // Preview overlay: draft values from the CMS win over the DB values.
  const post = isPreview ? { ...(fetchedPost || {}), ...draft } : fetchedPost;
  const related = (all || []).filter((n) => n.slug && n.slug !== slug).slice(0, 3);

  // Scroll to top whenever the reader navigates to a different article.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  if (loading && !isPreview) {
    return (
      <div className="pt-[140px] pb-40 text-center text-foreground/50 font-mono text-xs uppercase tracking-widest">
        Loading…
      </div>
    );
  }
  if ((error || !post) && !isPreview) {
    return (
      <div className="pt-[140px] pb-40 text-center" data-testid="news-detail-notfound">
        <p className="headline text-4xl text-ink">Story not found</p>
        <Link to="/news" className="mt-6 inline-block text-primary font-bold">← Back to news</Link>
      </div>
    );
  }
  if (!post) {
    // preview mode but the sessionStorage draft is gone (rare) — show a friendly message
    return (
      <div className="pt-[140px] pb-40 text-center">
        <p className="headline text-4xl text-ink">Preview unavailable</p>
        <p className="mt-4 text-foreground/70">Reopen Preview from the CMS to see the draft.</p>
      </div>
    );
  }

  const url = `${SITE}/news/${post.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: post.title,
    description: post.excerpt || undefined,
    image: post.image ? [post.image] : undefined,
    datePublished: post.date,
    dateModified: post.updated_at || post.date,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    publisher: {
      "@type": "Organization",
      name: "Which Media",
      logo: { "@type": "ImageObject", url: `${SITE}/logos/whichmedia-white2.png` },
    },
    articleSection: post.category,
  };
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE },
      { "@type": "ListItem", position: 2, name: "News", item: `${SITE}/news` },
      { "@type": "ListItem", position: 3, name: post.title, item: url },
    ],
  };

  const sharePayload = encodeURIComponent(post.title);
  const shareUrl = encodeURIComponent(url);
  const share = [
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}` },
    { label: "X (Twitter)", href: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${sharePayload}` },
    { label: "Copy link", copy: true },
  ];

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
  };

  const bodyParagraphs = (post.body || "").split(/\n{2,}/).filter(Boolean);

  // Safe inline HTML: escape everything, then re-allow <em>, <strong>, <br>,
  // single returns as line breaks, [link text](https://url) and bare URLs.
  const renderInline = (text) => {
    let escaped = (text || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    // Stash markdown-style links first so the bare-URL pass can't re-link them
    const stashed = [];
    escaped = escaped.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)"']+)\)/g, (_, label, href) => {
      stashed.push(`<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-primary font-semibold underline underline-offset-2 hover:text-ink transition-colors">${label}</a>`);
      return `%%WMLINK${stashed.length - 1}%%`;
    });
    return escaped
      .replace(/&lt;(em|strong)&gt;/gi, "<$1>")
      .replace(/&lt;\/(em|strong)&gt;/gi, "</$1>")
      .replace(/&lt;br\s*\/?&gt;/gi, "<br />")
      .replace(/(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/g, (m) => {
        const href = m.startsWith("http") ? m : `https://${m}`;
        return `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-primary font-semibold underline underline-offset-2 hover:text-ink transition-colors">${m}</a>`;
      })
      .replace(/\n/g, "<br />")
      .replace(/%%WMLINK(\d+)%%/g, (_, i) => stashed[Number(i)]);
  };

  return (
    <div data-testid="news-detail-page" className={isPreview ? "pt-[48px]" : undefined}>
      {isPreview && <PreviewBanner label="News draft preview" />}
      <Seo
        title={post.title}
        path={`/news/${post.slug}`}
        description={post.excerpt || post.body?.slice(0, 180)}
        image={post.image}
        type="article"
        jsonLd={[jsonLd, breadcrumb]}
      />

      {/* Article header */}
      <article className="pt-[76px]">
        <div className="mx-auto max-w-[1000px] px-5 md:px-8 pt-12 md:pt-16 pb-8">
          <Link to="/news" className="inline-flex items-center gap-2 text-sm font-bold text-foreground/60 hover:text-primary transition-colors mb-8">
            <ArrowLeft size={16} /> All news
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <span className="kicker" data-testid="news-detail-category">{post.category}</span>
            <span className="inline-flex items-center gap-2 font-sans text-xs font-bold text-foreground/50">
              <Calendar size={14} /> {post.date}
            </span>
          </div>
          <h1 className="headline text-4xl md:text-6xl lg:text-7xl uppercase text-ink leading-[1.05]" data-testid="news-detail-title">
            {post.title}
          </h1>
          {post.excerpt && (
            <p className="mt-6 text-xl md:text-2xl font-medium leading-snug text-foreground/70 max-w-3xl">
              {post.excerpt}
            </p>
          )}
        </div>

        {post.image && (
          <FadeUp>
            <div className="mx-auto max-w-[1200px] px-5 md:px-8">
              <div className="ed-img aspect-[16/9] rounded-2xl overflow-hidden">
                <img src={post.image} alt={post.title} data-testid="news-detail-image" />
              </div>
            </div>
          </FadeUp>
        )}

        <div className="mx-auto max-w-[720px] px-5 md:px-8 py-14 md:py-20">
          {bodyParagraphs.length > 0 ? (
            bodyParagraphs.map((p, i) => (
              <p
                key={i}
                className="text-lg md:text-xl leading-relaxed text-foreground/85 mb-6"
                dangerouslySetInnerHTML={{ __html: renderInline(p) }}
              />
            ))
          ) : (
            <p className="text-lg md:text-xl leading-relaxed text-foreground/60 italic">
              Full story coming shortly.
            </p>
          )}

          {/* Share bar */}
          <div className="mt-12 pt-8 border-t border-border flex flex-wrap items-center gap-3">
            <span className="font-mono text-xs uppercase tracking-widest text-foreground/50 mr-2 flex items-center gap-2">
              <Share2 size={14} /> Share
            </span>
            {share.map((s) =>
              s.copy ? (
                <button key={s.label} onClick={copy} data-testid="news-share-copy" className="font-sans text-sm font-bold border-2 border-ink text-ink rounded-full px-4 py-2 hover:bg-ink hover:text-white transition-colors">
                  {s.label}
                </button>
              ) : (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" data-testid={`news-share-${s.label.split(" ")[0].toLowerCase()}`} className="inline-flex items-center gap-1.5 font-sans text-sm font-bold border-2 border-ink text-ink rounded-full px-4 py-2 hover:bg-ink hover:text-white transition-colors">
                  {s.label} <ArrowUpRight size={14} />
                </a>
              )
            )}
          </div>
        </div>
      </article>

      {/* Related */}
      {related.length > 0 && (
        <section className="bg-secondary">
          <div className="mx-auto max-w-[1400px] px-5 md:px-8 py-16 md:py-24">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="kicker mb-3">More from the newsroom</p>
                <h2 className="headline text-3xl md:text-5xl uppercase text-ink">Related stories</h2>
              </div>
              <Link to="/news" className="hidden md:inline-flex items-center gap-2 font-sans text-sm font-bold text-primary hover:text-ink transition-colors">
                All news <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((n) => (
                <Link key={n.id} to={`/news/${n.slug}`} data-testid={`related-${n.slug}`} className="group block">
                  <div className="ed-img aspect-[16/10] rounded-xl mb-4">
                    <img src={n.image || undefined} alt={n.title} />
                  </div>
                  <span className="kicker">{n.category}</span>
                  <h3 className="mt-2 headline text-xl text-ink group-hover:text-primary transition-colors leading-tight">
                    {n.title}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default NewsDetail;
